/**
 * @file Menu.js
 * @author Stanley Chen
 * @description Menu component that displays different food categories offered at
 * Kiku Japanese Steakhouse with tabs for navigation between menu sections
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import './Menu.css';


const Menu = () => {
    const location = useLocation();

    // 1. Initialize state immediately from location state, or default to 'hibachi'
    const [activeCategory, setActiveCategory] = useState(
        location.state?.activeCategory || 'hibachi'
    );

    useEffect(() => {
        // 2. Only update if the user navigates here from another link 
        // WHILE already on the page
        if (location.state && location.state.activeCategory) {
            setActiveCategory(location.state.activeCategory);
        }
    }, [location.key, location.state]);


    const menuCategories = [
        { id: 'lunch', name: 'LUNCH SPECIALS' },
        { id: 'hibachi', name: 'HIBACHI' },
        { id: 'kitchen', name: 'KITCHEN DINNERS' },
        { id: 'sushi', name: 'SUSHI' }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.5 }
        },
        exit: {
            opacity: 0,
            transition: { duration: 0.2 }
        }
    };

    const getImagePath = (itemName) => {
        // Map item names to image paths
        const imageMap = {
            // Kitchen starters
            "Swan Lake (From Kitchen)": "/images/swan-lake.jpg",
            "Gyoza": "/images/menu/gyoza.jpg",
            "Wasabi Shumai": "/images/menu/wasabi-shumai.jpg",
            "Crispy Calamari": "/images/menu/crispy-calamari-2.jpg",
            "Chicken Yakitori": "/images/menu/chicken-yakitori.jpg",
            "Rock Shrimp": "/images/menu/rock-shrimp.jpg",

            // Japanese Fried Rice
            "Shrimp & Vegetable Fried Rice": "/images/menu/shrimp-and-vegetable-fried-rice.jpg",

            // Kitchen dinners
            "Grilled Swordfish": "/images/menu/grilled-swordfish.jpg",
            "Basil Filet Mignon": "/images/menu/basil-filet-mignon.jpg",
            "Chilean Sea Bass": "/images/menu/chilean-seabass.jpg",
            "Grill King Wild Salmon": "/images/menu/grill-king-wild-salmon.jpg",
            "Ahi Tuna Steak": "/images/menu/ahi-tuna-steak.jpg",

            // Udon
            "Seafood Yaki Udon or Soba": "/images/menu/seafood-yaki-soba.jpg",

            // Special rolls
            "Pink Lady": "/images/menu/pink-lady.jpg",
            "Rock 'N Roll": "/images/menu/rocknroll.jpg",
            "New Orleans": "/images/menu/new-orleans.jpg",
            "Crazy Tuna": "/images/menu/crazy-tuna.jpg",
            "Bonzai": "/images/menu/bonzai.jpg",

            // Sushi entrees
            "Sashimi Dinner": "/images/menu/sashimi-dinner.jpg"
        };

        return imageMap[itemName] || null;
    };

    const MenuItem = ({ name, price, description = "" }) => {
        const imagePath = activeCategory === 'hibachi' ? null : getImagePath(name);

        return (
            <div className={`menu-item ${imagePath ? 'with-image' : ''}`}>
                {imagePath && (
                    <div className="item-image-container">
                        <img src={imagePath} alt={name} className="item-image" />
                    </div>
                )}
                <div className="item-content">
                    <div className="item-header">
                        <h3 className="item-name">{name}</h3>
                        <span className="item-price">{price}</span>
                    </div>
                    {description && <p className="item-description">{description}</p>}
                </div>
            </div>
        );
    };

    const SectionHeader = ({ title, subtitle = "" }) => (
        <div className="section-header">
            <h2>{title}</h2>
            {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>
    );

    const renderMenuContent = () => {
        switch (activeCategory) {
            case 'lunch':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="lunch"
                    >

                        <div className="menu-top-header">
                            <img src="/images/gallery/gallery-24.jpg" alt="Lunch Banner" className="background-image" />
                            <div className="image-text-overlay">
                                LUNCH
                            </div>
                        </div>
                        <div className="menu-section">
                            <SectionHeader
                                title="Lunch Box from Kitchen"
                                subtitle="Served with Miso Soup, California Roll, Tempura Vegetables, Shrimp & White Rice"
                            />

                            <div className="menu-items two-columns">

                                <MenuItem name="Steak Teriyaki" price="$14.00" />
                                <MenuItem name="Chicken Teriyaki" price="$14.00" />
                                <MenuItem name="Tofu Teriyaki" price="$12.00" />
                                <MenuItem name="Salmon Teriyaki" price="$14.00" />
                                <MenuItem name="Shrimp Teriyaki" price="$14.00" />
                                <MenuItem name="Chicken Teriyaki" price="$14.00" />
                                <MenuItem name="Vegetable Teriyaki" price="$12.00" />
                                <MenuItem name="Shrimp Tempura" price="$14.00" />
                                <MenuItem name="Beef Negimaki" price="$14.00" />
                                <MenuItem name="Chicken Katsu" price="$14.00" />
                                <MenuItem name="Ton Katsu" price="$14.00" />

                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader
                                title="Lunch from Sushi Bar"
                                subtitle="Served with Miso Soup"
                            />

                            <div className="menu-items">
                                <MenuItem
                                    name="Sushi Lunch"
                                    price="$13.00"
                                    description="5 pieces of sushi and a California roll"
                                />
                                <MenuItem
                                    name="Sashimi Lunch"
                                    price="$15.00"
                                    description="12 pieces of assorted sashimi & steamed rice"
                                />
                                <MenuItem
                                    name="Sushi & Sashimi Combo"
                                    price="$20.00"
                                    description="4 pieces of sushi, 8 pieces of sashimi & tuna roll"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader
                                title="Lunch from the Hibachi"
                                subtitle="Served with Clear Soup & Choice of Fried Rice, White Rice or Noodles"
                            />

                            <div className="menu-items two-columns">
                                <MenuItem name="Hibachi Chicken" price="$14.00" />
                                <MenuItem name="Hibachi Salmon" price="$14.00" />
                                <MenuItem name="Hibachi Vegetable" price="$12.00" />
                                <MenuItem name="Hibachi Steak" price="$14.00" />
                                <MenuItem name="Hibachi Shrimp" price="$14.00" />
                                <MenuItem name="Hibachi Scallop" price="$16.00" />
                                <MenuItem name="Hibachi Filet Mignon" price="$16.00" />
                            </div>

                            <div className="menu-note">
                                <p>Hibachi Lunch can be combined with Side Orders to make Combo:</p>
                                <p>Chicken $8.00 | Shrimp $7.00 | Salmon $8.00 | Scallop $12.00 | Steak $10.00 | Filet Mignon $12.00 | Lobster $16.00</p>
                            </div>

                        </div>

                        <div className="menu-section">
                            <SectionHeader
                                title="Maki Lunch"
                                subtitle="Any 2 Rolls for $11.00 or Any 3 Rolls for $13.00. Served with Miso Soup"
                            />

                            <div className="menu-columns">
                                <div className="menu-column">
                                    <h3>Raw Rolls</h3>
                                    <ul>
                                        <li>Tuna Roll</li>
                                        <li>Salmon Roll</li>
                                        <li>Yellowtail Roll</li>
                                        <li>Spicy Tuna Roll</li>
                                        <li>Spicy Salmon Roll</li>
                                        <li>Spicy Yellowtail Roll</li>
                                        <li>Alaska Roll</li>
                                        <li>Tuna Avocado Roll</li>
                                        <li>Salmon Avocado Roll</li>
                                    </ul>
                                </div>

                                <div className="menu-column">
                                    <h3>Cooked Rolls</h3>
                                    <ul>
                                        <li>Spicy Shrimp Roll</li>
                                        <li>Shrimp Tempura Roll</li>
                                        <li>California Roll</li>
                                        <li>Philadelphia Roll</li>
                                        <li>Boston Roll</li>
                                        <li>Eel Avocado Roll</li>
                                        <li>Eel Cucumber Roll</li>
                                        <li>Spicy Crab Roll</li>
                                        <li>Sweet Potato Roll</li>
                                    </ul>
                                </div>

                                <div className="menu-column">
                                    <h3>Vegetable Rolls</h3>
                                    <ul>
                                        <li>Avocado Roll</li>
                                        <li>Cucumber Roll</li>
                                        <li>Asparagus Roll</li>
                                        <li>Vegetable Roll</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                );

            case 'hibachi':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="hibachi"
                    >
                        <div className="menu-top-header">
                            <img src="/images/gallery/gallery-21.jpg" alt="Hibachi Banner" className="background-image" />
                            <div className="image-text-overlay">
                                HIBACHI
                            </div>
                        </div>




                        <div className="menu-section">
                            <SectionHeader
                                title="Hibachi Dinner"
                                subtitle="Served with Two Shrimp Appetizer, Vegetable & Rice or Noodles (Soup or Salad)"
                            />

                            <div className="menu-note">
                                <p>Dine in only: Included with your meal, your chef may offer tastes of Saké (Japanese rice wine containing alcohol) to guests that are 21 and older. Those avoiding alcohol and children may be offered "Baby Saké" (lemonade) instead.</p>
                            </div>

                            <div className="menu-items two-columns">
                                <MenuItem name="Ahi Tuna" price="$30.00" />
                                <MenuItem name="Jumbo Shrimp" price="$24.00" />
                                <MenuItem name="Scallop" price="$30.00" />
                                <MenuItem name="Twin Lobster Tail" price="$36.00" />
                                <MenuItem name="Chicken" price="$20.00" />
                                <MenuItem name="Filet Mignon" price="$26.00" />
                                <MenuItem name="Tilapia" price="$20.00" />
                                <MenuItem name="Chilean Sea Bass" price="$30.00" />
                                <MenuItem name="Swordfish" price="$24.00" />
                                <MenuItem name="Salmon" price="$26.00" />
                                <MenuItem name="Vegetable Hibachi" price="$15.00" />
                                <MenuItem name="Angus Steak" price="$24.00" />
                                <MenuItem name="Rack of Lamb" price="$30.00" />
                            </div>
                        </div>


                        <div className="menu-section">
                            <SectionHeader
                                title="Hibachi Combinations"
                                subtitle="Served with Two Shrimp Appetizer, Vegetable & Rice or Noodles"
                            />

                            <div className="menu-items">
                                <MenuItem
                                    name="Any Two Items"
                                    price="$26.00"
                                    description="Choice of Scallop, Salmon, Swordfish, Jumbo Shrimp, Chicken, Angus Steak"
                                />
                                <MenuItem
                                    name="Any Three Items"
                                    price="$34.00"
                                    description="Choice of Scallop, Salmon, Swordfish, Jumbo Shrimp, Chicken, Angus Steak"
                                />
                            </div>

                            <div className="menu-note">
                                <p>Upgrade and substitute one of your items:</p>
                                <p>+$3.00 for Rack of Lamb or Filet Mignon | +$6.00 for Lobster Tail</p>

                            </div>



                        </div>



                        <div className="menu-section">
                            <SectionHeader title="Hibachi Side Orders" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Hibachi Fried Rice" price="$5.00" />
                                <MenuItem name="Hibachi Chicken" price="$7.00" />
                                <MenuItem name="Hibachi Lobster" price="$16.00" />
                                <MenuItem name="Hibachi Noodles" price="$4.00" />
                                <MenuItem name="Hibachi Scallop" price="$14.00" />
                                <MenuItem name="Hibachi Shrimp" price="$6.00" />
                                <MenuItem name="Hibachi Steak" price="$10.00" />
                                <MenuItem name="Hibachi Vegetables" price="$5.00" />
                                <MenuItem name="Hibachi Salmon" price="$8.00" />
                                <MenuItem name="Hibachi Filet Mignon" price="$12.00" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Hibachi Kids Meal" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Hibachi Vegetables" price="$8.00" />
                                <MenuItem name="Hibachi Salmon" price="$14.00" />
                                <MenuItem name="Hibachi Angus Steak" price="$14.00" />
                                <MenuItem name="Hibachi Shrimp" price="$14.00" />
                                <MenuItem name="Hibachi Chicken" price="$12.00" />
                                <MenuItem name="Hibachi Filet Mignon" price="$16.00" />
                            </div>
                        </div>
                    </motion.div>
                );

            case 'sushi':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="sushi"
                    >

                        <div className="menu-top-header">
                            <img src="/images/gallery/gallery-23.jpg" alt="Sushi Banner" className="background-image" />
                            <div className="image-text-overlay">
                                SUSHI
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Appetizers" />
                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Tuna Ravioli"
                                    price="$10.00"
                                    description="Tuna made up w/ skin, avocado & wasabi tobiko"
                                />
                                <MenuItem
                                    name="Pepper Tuna Tataki"
                                    price="$11.00"
                                    description="Seared pepper tuna w/ creamy wasabi sauce"
                                />
                                <MenuItem
                                    name="Sashimi Jalapeno"
                                    price="$12.00"
                                    description="Jalapeno peppers over two pieces each of tuna, salmon, yellowtail then finished w/ avocado & Jalapeno sauce"
                                />
                                <MenuItem
                                    name="Salmon Ikura Wrap"
                                    price="$13.00"
                                    description="Fresh Scottish salmon wrapped around rice & salmon roe topped w/ black tobiko"
                                />
                                <MenuItem
                                    name="Tuna Tartar or Salmon Tartar"
                                    price="$11.00"
                                    description="Chop tuna or salmon w/ caviar, quail egg & spicy sauce"
                                />
                                <MenuItem
                                    name="Rainbow Naruto"
                                    price="$15.00"
                                    description="Thinly peeled whole cucumber, wrapped w/ tuna, salmon, yellowtail, crab meat"
                                />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Signature Rolls" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Pink Lady" price="$15.00" description="Crunchy lobster salad, avocado & mango rolled in soy bean wrapper w/ creamy wasabi sauce" />
                                <MenuItem name="Rock 'N Roll" price="$11.00" description="Crispy tempura roll w/ smoked salmon, cream cheese, avocado & drizzled w/ spicy sweet sauce" />
                                <MenuItem name="New Orleans" price="$16.00" description="Yellowtail salmon, cucumber, Ahi tuna w/ orange tobiko" />
                                <MenuItem name="Crazy Tuna" price="$15.00" description="Crunchy spicy tuna topped w/ pepper tuna avocado & flying fish roe" />
                                <MenuItem name="Bonzai" price="$16.00" description="Pepper tuna, salmon & avocado, crunchy lobster salad & Jalapeno sauce" />
                                <MenuItem name="Christmas" price="$14.00" description="Spicy tuna roll topped w/ a bed of avocado & flying fish roe" />
                                <MenuItem name="Hidden Dragon" price="$15.00" description="Two shrimp tempura topped w/ BBQ fresh water eel & avocado" />
                                <MenuItem name="Angry Dragon" price="$16.00" description="Shrimp tempura, mango, spicy tuna inside w/ spicy king crab on top w/ Ruta sauce" />
                                <MenuItem name="Fish Finder" price="$18.00" description="Super white tuna and cucumber inside, topped with tuna, yellowtail, and salmon with yuzu sauce" />
                                <MenuItem name="Dream Team Roll" price="$15.00" description="Ahi tuna & black tobiko on top of crunchy spicy scallop w/ chef special sauce" />
                                <MenuItem name="Honeymoon" price="$15.00" description="Ahi tuna & salmon on top of coconut white fish, asparagus w/ honey wasabi sauces" />
                                <MenuItem name="Fire Dragon" price="$14.00" description="Deep fried crab meat w/ spicy tuna w/ red topiko on top" />
                                <MenuItem name="Fancy Alaska" price="$17.00" description="Crab, asparagus, spicy sauce w/ scallop and salmon" />
                                <MenuItem name="Sex Appeal" price="$16.00" description="Salmon. spicy yellowtail, avocado, crunch, tuna, and tobiko" />
                                <MenuItem name="Dancing Tuna" price="$15.00" description="Spicy tuna topped w/ bluefin tuna and crab meat" />
                                <MenuItem name="Hatsuhana" price="$17.00" description="Spicy shrimp, crab, crunch, and avocado inside, yellowtail, salmon, and tobiko on top" />
                                <MenuItem name="Brazilian" price="$17.00" description="Steamed brazilian lobster, cucumber, filet mignon on top torched and seared" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Sushi Entrees" />

                            <div className="menu-items">
                                <MenuItem name="Sushi Dinner" price="$24.00" description="9 pieces of sushi w/ spicy tuna roll" />
                                <MenuItem name="Sashimi Dinner" price="$27.00" description="18 pieces of sashimi" />
                                <MenuItem name="Sushi & Sashimi Combo" price="$27.00" description="12 pieces of sashimi & 5 pieces of sushi" />
                                <MenuItem name="Charishi Dinner" price="$27.00" description="Assorted fish on a bowl of seasoned rice" />
                                <MenuItem name="Sake Don (Salmon)" price="$25.00" description="13 pieces of salmon on a bowl of seasonal rice" />
                                <MenuItem name="Unagi Don (Eel)" price="$22.00" description="Broiled eel on a bowl of seasoned rice" />
                                <MenuItem name="Tekka Don (Tuna)" price="$27.00" description="13 pieces of tuna on a bowl of seasoned rice" />
                                <MenuItem name="Love Boat" price="$52.00" description="16 pieces of sushi w/ a California roll, spicy tuna roll & shrimp tempura roll" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Sushi or Sashimi (A La Carte)" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Eel (Unagi)" price="$4.00" />
                                <MenuItem name="Squid (Ika)" price="$3.00" />
                                <MenuItem name="Surf Clam" price="$3.00" />
                                <MenuItem name="King Salmon" price="$4.00" />
                                <MenuItem name="Shrimp (Ebi)" price="$3.00" />
                                <MenuItem name="Scallop (Hotate)" price="$5.00" />
                                <MenuItem name="Octopus (Tako)" price="$4.00" />
                                <MenuItem name="Tuna (Maguro)" price="$4.00" />
                                <MenuItem name="Salmon (Sake)" price="$3.00" />
                                <MenuItem name="Sea Urchin (Uni)" price="$6.00" />
                                <MenuItem name="Albacore Tuna" price="$3.00" />
                                <MenuItem name="Smoked Salmon" price="$3.00" />
                                <MenuItem name="Tuna Belly (Toro)" price="$12.00" />
                                <MenuItem name="Mackerel (Saba)" price="$3.00" />
                                <MenuItem name="Bean Curd (Inari)" price="$3.00" />
                                <MenuItem name="Yellowtail (Hamachi)" price="$4.00" />
                                <MenuItem name="White Tuna (Abura)" price="$3.00" />
                                <MenuItem name="Salmon Roe (Ikura)" price="$4.00" />
                                <MenuItem name="Stripe Bass (Suzuki)" price="$3.00" />
                                <MenuItem name="Crab Stick (Kani Kama)" price="$3.00" />
                                <MenuItem name="Egg Custard (Tomago)" price="$3.00" />
                                <MenuItem name="Spanish Mackerel (Aji)" price="$3.00" />
                                <MenuItem name="Flying Fish Roe (Tobikio)" price="$3.00" />
                                <MenuItem name="Red Snapper (Mai Dai/Tai)" price="$4.00" />
                                <MenuItem name="Jumbo Shrimp (Bontan Ebi)" price="$6.00" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Classic Rolls or Temaki (Hand Roll)" />

                            <div className="menu-items two-columns">
                                <MenuItem name="California Roll" price="$6.00" description="Crabmeat, avocado, cucumber & tobiko" />
                                <MenuItem name="Boston Roll" price="$6.00" description="Lettuce, shrimp, cucumber & mayo" />
                                <MenuItem name="Alaska Roll" price="$6.00" description="Salmon, avocado & cucumber" />
                                <MenuItem name="Philadelphia Roll" price="$6.00" description="Smoked salmon, cream cheese & cucumber" />
                                <MenuItem name="Easter Roll" price="$6.00" description="Smoked salmon, crab meat & cucumber" />
                                <MenuItem name="Shrimp Tempura Roll" price="$7.00" description="Shrimp gently fried w/ cucumber, avocado & flying fish roe" />
                                <MenuItem name="Chicken Tempura Roll" price="$6.00" description="Chicken gently fried w/ cucumber avocado & flying fish roe" />
                                <MenuItem name="Dragon Roll" price="$13.00" description="Eel & cucumbers roll topped with avocado" />
                                <MenuItem name="Rainbow Roll" price="$13.00" description="California roll topped w/ tuna, white fish, salmon & avocado" />
                                <MenuItem name="Spicy Roll" price="$6.50" description="Choice of tuna, salmon, yellowtail, shrimp, or crab meat w/ crunch" />
                                <MenuItem name="Salmon Skin Roll" price="$6.00" description="Broiled crispy salmon skin w/ cucumber" />
                                <MenuItem name="Fish Rolls" price="$6.00" description="Choice of tuna, salmon, yellowtail, smoked salmon, white tuna, or shrimp" />
                                <MenuItem name="Spider Roll" price="$11.00" />
                                <MenuItem name="Tuna Avocado Roll" price="$7.00" />
                                <MenuItem name="Salmon Avocado Roll" price="$7.00" />
                                <MenuItem name="Eel Avocado Roll" price="$7.00" />
                                <MenuItem name="Spicy Scallop Roll" price="$8.00" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Vegetable Rolls" />

                            <div className="menu-items">
                                <MenuItem name="Fried Sweet Potato Roll" price="$5.00" />
                                <MenuItem name="Avocado Cucumber Roll" price="$5.00" />
                                <MenuItem name="Avocado Roll" price="$4.00" />
                                <MenuItem name="Green Roll" price="$5.00" description="Fried broccoli roll w/ sauce" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Party Platters" />

                            <div className="menu-items">
                                <MenuItem name="Party A" price="$43.00" description="Spider roll, Rock'n roll, spicy tuna roll, easter roll, hidden dragon roll" />
                                <MenuItem name="Party B" price="$64.00" description="18 pieces of sashimi, 12 pieces sushi with rainbow roll, New Orleans roll" />
                                <MenuItem name="Party C" price="$65.00" description="Rainbow roll, crazy tuna roll, hidden dragon roll, Rock'n roll, eel avocado roll, Alaska roll, shrimp tempura roll, spicy shrimp roll, spicy crab roll" />
                                <MenuItem name="Party D" price="$72.00" description="Christmas roll, crazy tuna roll, pink lady roll, New Orleans roll, fish finder roll, angry dragon roll" />
                                <MenuItem name="Party E" price="$85.00" description="24 pieces of sashimi, 15 pieces of sushi, Christmas roll, New Orleans roll, shrimp tempura roll" />
                            </div>
                        </div>

                    </motion.div>
                );

            case 'kitchen':
                return (
                    <motion.div
                        className="menu-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        key="kitchen"
                    >

                        <div className="menu-top-header">
                            <img src="/images/gallery/gallery-8.jpg" alt="Kitchen Banner" className="background-image" />
                            <div className="image-text-overlay">
                                KITCHEN
                            </div>
                        </div>
                        <div className="menu-section">
                            <SectionHeader title="Kitchen Starters" />

                            <div className="menu-items two-columns">
                                <MenuItem
                                    name="Swan Lake (From Kitchen)"
                                    price="15.00"
                                    description="Crispy skin rolled Beijing duck, green onion & cucumber"
                                />
                                <MenuItem
                                    name="Crispy Calamari"
                                    price="9.00"
                                    description="Tempura battered calamari"
                                />

                                <MenuItem
                                    name="Wasabi Shumai"
                                    price="8.00"
                                    description="6 pieces steamed pork wasabi skin dumplings"
                                />

                                <MenuItem
                                    name="Gyoza"
                                    price="6.00"
                                    description="6 pan fried chicken or vegetable dumplings"
                                />

                                <MenuItem
                                    name="Chicken Yakitori"
                                    price="6.00"
                                    description="Grilled curry chicken on a bamboo stick"
                                />

                                <MenuItem
                                    name="Rock Shrimp"
                                    price="8.00"
                                    description="Bite-sized tempura shrimp tossed w/ sweet & spicy mayo"
                                />
                                <MenuItem
                                    name="Hurricane Prawns"
                                    price="8.00"
                                    description="4 pieces gently fried jumbo shrimp rolled w/ kataifi noodles w/ sweet hot sauce"
                                />

                                <MenuItem
                                    name="Soft Shell Crab"
                                    price="11.00"
                                    description="Pan fried crab w/ special sauce"
                                />
                                <MenuItem
                                    name="Shrimp or Chicken Tempura"
                                    price="8.00"
                                    description="Shrimp or chicken fried w/ tempura batter | Vegetable Tempura Available Only $6.00"
                                />
                                <MenuItem
                                    name="Shrimp Shumai"
                                    price="6.00"
                                    description="4 piece steamed shrimp dumplings"
                                />

                                <MenuItem
                                    name="Japanese Spring Roll"
                                    price="6.00"
                                    description="Crabmeat and asparagus with cheddar and mozzarella cheeses inside a crispy wrap"
                                />


                                <MenuItem
                                    name="Sauteed Broccoli"
                                    price="6.00"
                                    description=""
                                />
                                <MenuItem
                                    name="Yaki Ika"
                                    price="13.00"
                                    description="Grilled squid w/ teriyaki sauce"
                                />
                                <MenuItem
                                    name="Age Dashi Tofu"
                                    price="6.00"
                                    description="Deep fried tofu w/ special sauce"
                                />
                                <MenuItem
                                    name="Beef Negimaki"
                                    price="8.00"
                                    description="6 pieces thinly sliced beef wrapped around scallions and broiled"
                                />
                                <MenuItem
                                    name="Coconut Shrimp"
                                    price="8.00"
                                    description="4 pieces gently fried coconut shrimp w/ Thai style sweet hot sauce"
                                />
                                <MenuItem
                                    name="Edamame"
                                    price="8.00"
                                    description="Steamed salted soybean pods"
                                />
                                <MenuItem
                                    name="Dinosaur Eggs"
                                    price="8.00"
                                    description="Fried oysters w/ special sauce"
                                />

                            </div>

                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Soup or Salad" />


                            <div className="menu-items">
                                <MenuItem name="Miso Soup" price="$2.00" description="Soy broth w/ seaweed, scallions & tofu" />
                                <MenuItem name="Clear Soup" price="$2.50" description="Chicken/beef broth w/ fried onions & scallions" />
                                <MenuItem name="Seafood Miso Soup" price="$8.00" description="Shrimp, scallop, crab meat & fish cake" />
                                <MenuItem name="Tom Yum Shrimp Soup" price="$8.00" description="Hot & sour soup w/ shrimp, tomato & a splash of lime" />
                                <MenuItem name="House Green Salad" price="$2.50" description="" />
                                <MenuItem name="House Green Salad w/ Avocado" price="$6.00" description="" />
                                <MenuItem name="Seaweed Salad" price="$6.00" description="" />
                                <MenuItem name="Baby Octopus Salad" price="$8.00" description="" />
                                <MenuItem name="Tuna Martini Salad" price="$10.00" description="Spicy tuna, fried crab w/ special sauce" />
                                <MenuItem name="Ocean Salad" price="$10.00" description="Seaweed salad, crab, cucumber, spicy tuna w/ spicy mayo & ponzu sauce" />
                            </div>
                        </div>
                        <div className="menu-section">
                            <SectionHeader title="Kitchen Dinner Entrees" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Ahi Tuna Steak" price="$30.00" description="Served ahi tuna w/ shrimp & mixed vegetables" />
                                <MenuItem name="Grill King Wild Salmon" price="$30.00" description="Grilled wild salmon w/ mixed vegetables & asparagus" />
                                <MenuItem name="Chilean Sea Bass" price="$30.00" description="Pan steamed sea bass w/ black bean sauce" />
                                <MenuItem name="Basil Filet Mignon" price="$26.00" description="Stir-fried filet mignon w/ onions, pepper & basil" />
                                <MenuItem name="Grilled Swordfish" price="$20.00" description="Grilled swordfish w/ mixed vegetables" />
                                <MenuItem name="Hawaii Style Red Snapper" price="$20.00" description="Mango, pineapple w/ Hawaii special sauce" />
                                <MenuItem name="Golden Mango Shrimp" price="$24.00" description="Lightly fried shrimp, stir fry w/ mango, asparagus, avocado & sweet red pepper" />
                                <MenuItem name="Szechuan Spicy Steak" price="$26.00" description="Jalepeno and szechuan pepper, steak w/ mixed vegetables" />
                            </div>
                        </div>
                        <div className="menu-section">
                            <SectionHeader title="Udon or Soba" />

                            <div className="menu-items">
                                <MenuItem name="Seafood Yaki Udon or Soba" price="$19.00" description="Stir fried udon or soba w/ shrimp, scallops, fish patty & mixed vegetables" />
                                <MenuItem name="Chicken or Beef Yaki Udon or Soba" price="$15.00" description="Stir fried udon or soba w/ chicken (or beef) & mixed vegetables" />
                                <MenuItem name="Vegetable Yaki Udon or Soba" price="$13.00" description="Stir fried udon or soba w/ mixed vegetables" />
                                <MenuItem name="Nebeyaki Udon or Soba (Soup)" price="$16.00" description="Chicken, fish patty, broiled egg, vegetables & udon or soba in chicken broth served w/ two pieces of shrimp tempura on the side" />
                                <MenuItem name="Seafood Udon or Soba (Soup)" price="$18.00" description="Jumbo shrimp, scallops, fish patty, vegetables & udon or soba in fish broth" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Japanese Fried Rice" />

                            <div className="menu-items">
                                <MenuItem name="Shrimp & Vegetable Fried Rice" price="$14.00" description="" />
                                <MenuItem name="Vegetable Fried Rice" price="$12.00" description="" />
                                <MenuItem name="Vegetable Fried Rice w/ Beef or Chicken" price="$14.00" description="" />
                                <MenuItem name="Seafood & Pineapple Fried Rice" price="$16.00" description="" />
                                <MenuItem name="Combo Fried Rice" price="$16.00" description="" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Teriyaki" />

                            <div className="menu-items two-columns">
                                <MenuItem name="Chicken Teriyaki" price="$20.00" description="" />
                                <MenuItem name="Steak Teriyaki" price="$24.00" description="" />
                                <MenuItem name="Salmon Teriyaki" price="$24.00" description="" />
                                <MenuItem name="Seafood Teriyaki" price="$30.00" description="" />
                                <MenuItem name="Shrimp Teriyaki" price="$24.00" description="" />
                                <MenuItem name="Vegetable Teriyaki" price="$15.00" description="" />
                                <MenuItem name="Tofu Teriyaki" price="$15.00" description="" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Tempura" />

                            <div className="menu-items">
                                <MenuItem name="Shrimp or Chicken Tempura" price="$17.00" description="" />
                                <MenuItem name="Assorted Tempura" price="$20.00" description="Shrimp, chicken & vegetable" />
                                <MenuItem name="Seafood Tempura" price="$30.00" description="Shrimp, scallops, crab stick, white fish & vegetable" />
                                <MenuItem name="Vegetable Tempura" price="$15.00" description="" />
                            </div>
                        </div>

                        <div className="menu-section">
                            <SectionHeader title="Katsu" />

                            <div className="menu-items">
                                <MenuItem name="Ton Katsu (Pork)" price="$20.00" description="Gently Fried & Served with Katsu Sauce" />
                                <MenuItem name="Chicken Katsu" price="$20.00" description="Gently Fried & Served with Katsu Sauce" />
                            </div>
                        </div>


                    </motion.div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="menu-page">
            <div className="menu-header">
                <h1>OUR MENU</h1>

                <div className="menu-tabs">
                    {menuCategories.map(category => (
                        <button
                            key={category.id}
                            className={`menu-tab ${activeCategory === category.id ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category.id)}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>

                <p className="menu-note alert">
                    Japanese Cuisine Contains Nuts, Sesame, Soy, Beans, Garlic, Onions, Ginger etc. in many ingredients. Please inform us if you are allergic to any of these ingredients.
                </p>

            </div>

            <div className="menu-container">
                <AnimatePresence mode="wait">
                    {renderMenuContent()}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default Menu;